import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Sparkles,
  MailOpen,
  BadgeCheck,
  LogOut,
  Loader2,
  Search,
  Clock,
  Share2,
  Trash2,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { ASSETS } from "../../data/site";
import { useAdminAuth, adminAxios } from "../../context/AdminAuth";
import { VerificationTab } from "./VerificationTab";
import { AppStorePanel } from "./AppStorePanel";
import { AppUpdatesTab } from "./AppUpdatesTab";

const TABS = [
  { key: "waitlist", label: "Waitlist", icon: Users },
  { key: "beta", label: "Beta Users", icon: Sparkles },
  { key: "contacts", label: "Contact Messages", icon: MailOpen },
  { key: "verification", label: "Verification", icon: BadgeCheck },
  { key: "app-updates", label: "App Updates", icon: Smartphone },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#2F5E43]/10 bg-white p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
    <div className="flex items-center justify-between">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E4EDE6] text-[#2F5E43]">
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <p className="mt-4 font-heading text-3xl font-semibold text-[#111E16]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      {value}
    </p>
    <p className="mt-1 text-sm text-[#4A5F52]">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("waitlist");
  const [stats, setStats] = useState({ waitlist: 0, beta: 0, contacts: 0, verifications: 0 });
  const [referrals, setReferrals] = useState({ referred_total: 0, channels: {}, top_referrers: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  useEffect(() => {
    adminAxios.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    adminAxios.get("/admin/referral-stats").then((r) => setReferrals(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "verification") {
      setRows([]);
      return;
    }
    setLoading(true);
    setQuery("");
    const req =
      tab === "contacts"
        ? adminAxios.get("/admin/contacts")
        : adminAxios.get("/admin/signups", { params: { signup_type: tab } });
    req
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const filtered = rows.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.subject || "").toLowerCase().includes(q) ||
      (r.message || "").toLowerCase().includes(q)
    );
  });

  const deleteSignup = async (id) => {
    if (!window.confirm("Delete this signup? This cannot be undone.")) return;
    try {
      await adminAxios.delete(`/admin/signups/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Signup deleted");
      adminAxios.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch {
      toast.error("Could not delete signup");
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[#2F5E43]/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-2.5">
            <img src={ASSETS.logoIcon} alt="SisterSafe" className="h-8 w-8 object-contain" />
            <span className="font-heading text-lg font-semibold tracking-tight text-[#111E16]">
              SisterSafe <span className="text-[#8AA583]">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-[#4A5F52] sm:block" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-2 rounded-full border border-[#E8EBE9] bg-white px-4 py-2 text-sm font-medium text-[#2F5E43] transition-colors hover:bg-[#F2F5F2]"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-[#111E16]">
          Dashboard
        </h1>
        <p className="mt-1.5 text-[#4A5F52]">Overview of signups and messages.</p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Waitlist" value={stats.waitlist} />
          <StatCard icon={Sparkles} label="Beta Users" value={stats.beta} />
          <StatCard icon={MailOpen} label="Messages" value={stats.contacts} />
          <StatCard icon={BadgeCheck} label="Verifications" value={stats.verifications} />
        </div>

        {/* Referrals */}
        <div className="mt-6 rounded-2xl border border-[#2F5E43]/10 bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="referrals-panel">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E4EDE6] text-[#2F5E43]">
              <Share2 className="h-4.5 w-4.5" />
            </span>
            <h2 className="font-heading text-lg font-semibold text-[#111E16]">Referrals</h2>
            <span className="ml-auto text-sm text-[#4A5F52]">
              <span className="font-semibold text-[#2F5E43]" data-testid="referred-total">{referrals.referred_total}</span> signups via referral
            </span>
          </div>

          <div className="mt-5 grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8AA583]">By channel</p>
              {Object.keys(referrals.channels || {}).length === 0 ? (
                <p className="mt-3 text-sm text-[#4A5F52]">No referral signups yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {Object.entries(referrals.channels).sort((a, b) => b[1] - a[1]).map(([ch, n]) => (
                    <div key={ch} className="flex items-center justify-between text-sm" data-testid={`channel-${ch}`}>
                      <span className="capitalize text-[#111E16]">{ch}</span>
                      <span className="font-semibold text-[#2F5E43]">{n}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8AA583]">Top referrers</p>
              {(referrals.top_referrers || []).length === 0 ? (
                <p className="mt-3 text-sm text-[#4A5F52]">No referrers yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {referrals.top_referrers.map((r) => (
                    <div key={r.code} className="flex items-center justify-between text-sm">
                      <span className="truncate text-[#111E16]">{r.name || r.email || r.code}</span>
                      <span className="font-semibold text-[#2F5E43]">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <AppStorePanel />

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2 border-b border-[#E8EBE9]">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  active
                    ? "border-[#2F5E43] text-[#2F5E43]"
                    : "border-transparent text-[#4A5F52] hover:text-[#2F5E43]"
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-6">
          {tab === "verification" ? (
            <VerificationTab />
          ) : tab === "app-updates" ? (
            <AppUpdatesTab />
          ) : (
            <>
              <div className="mb-4 flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8AA583]" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search…"
                    className="w-full rounded-full border border-[#E8EBE9] bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-[#2F5E43]/30"
                    data-testid="admin-search"
                  />
                </div>
                <span className="text-sm text-[#8AA583]">{filtered.length} records</span>
              </div>

              <div className="overflow-hidden rounded-2xl border border-[#E8EBE9] bg-white">
                {loading ? (
                  <div className="flex items-center justify-center py-20 text-[#4A5F52]">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-20 text-center text-[#8AA583]" data-testid="admin-empty">
                    No records yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm" data-testid="admin-table">
                      <thead className="bg-[#F2F5F2] text-xs uppercase tracking-wider text-[#8AA583]">
                        <tr>
                          <th className="px-5 py-3 font-semibold">Name</th>
                          <th className="px-5 py-3 font-semibold">Email</th>
                          {tab === "contacts" && (
                            <>
                              <th className="px-5 py-3 font-semibold">Subject</th>
                              <th className="px-5 py-3 font-semibold">Message</th>
                            </>
                          )}
                          <th className="px-5 py-3 font-semibold">Date</th>
                          {tab !== "contacts" && (
                            <th className="px-5 py-3 font-semibold text-right">Action</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0F2F0]">
                        {filtered.map((r, i) => (
                          <tr key={r.id || i} className="hover:bg-[#FBFBF9]" data-testid="admin-row">
                            <td className="px-5 py-4 font-medium text-[#111E16]">{r.name}</td>
                            <td className="px-5 py-4 text-[#4A5F52]">{r.email}</td>
                            {tab === "contacts" && (
                              <>
                                <td className="px-5 py-4 text-[#111E16]">{r.subject}</td>
                                <td className="px-5 py-4 max-w-xs truncate text-[#4A5F52]" title={r.message}>
                                  {r.message}
                                </td>
                              </>
                            )}
                            <td className="whitespace-nowrap px-5 py-4 text-[#8AA583]">
                              {fmtDate(r.created_at)}
                            </td>
                            {tab !== "contacts" && (
                              <td className="whitespace-nowrap px-5 py-4 text-right">
                                <button
                                  onClick={() => deleteSignup(r.id)}
                                  aria-label={`Delete ${r.email}`}
                                  title="Delete"
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#8AA583] transition-colors hover:bg-red-50 hover:text-red-600"
                                  data-testid={`delete-signup-${r.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
